import axios from 'axios'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { constants } from '../Constants'
import { Button, Modal } from '../components/ui'
import { PageLoader } from '../components/ui/Spinner/Spinner'
import { useToast } from '../components/ui/Toast/Toast'
import '../styles/shared.scss'
import './Schedule.scss'

// Los fines de semana no se usan nunca, por lo que no se muestran en el editor.
const dayNames = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes']

// Convierte el horario del formato antiguo (1=Lun...6=Sáb, 0=Dom)
// al nuevo (0=Lun...5=Sáb, 6=Dom).
const migrateScheduleFormat = schedule => {
  const result = {}
  for (const [cat, days] of Object.entries(schedule)) {
    result[cat] = {}
    for (const [key, times] of Object.entries(days)) {
      const k = parseInt(key, 10)
      const newKey = k === 0 ? 6 : k - 1
      result[cat][String(newKey)] = times
    }
  }
  return result
}

export const Schedule = () => {
  const [schedules, setSchedules] = useState({})
  const [scheduleId, setScheduleId] = useState(null)
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [generateOpen, setGenerateOpen] = useState(false)
  const [weeks, setWeeks] = useState(1)
  const [cleanFirst, setCleanFirst] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [cronConfig, setCronConfig] = useState({ dayOfWeek: 4, weeksAhead: 1 })
  const toast = useToast()
  const navigate = useNavigate()

  // Solo los administradores pueden gestionar horarios y generar clases
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || 'null')
    if (!user || user.role !== 'admin') {
      navigate('/')
    }
  }, [navigate])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [schedRes, catRes] = await Promise.all([
        axios.get(`${constants.urlLocal}schedule/get`),
        axios.get(`${constants.urlLocal}categories/getAll`),
      ])
      const sData = schedRes.data.schedules || []
      if (sData.length > 0) {
        const raw = sData[0].schedule || {}
        // El formato ahora es una marca explícita del documento (scheduleFormat),
        // no una suposición por categoría: adivinarlo categoría a categoría era
        // justo lo que causaba que se generase una clase en domingo y se
        // perdiera la del viernes en el back (ver src/controller/event.js).
        const normalized =
          sData[0].scheduleFormat === 'old' ? migrateScheduleFormat(raw) : raw
        setSchedules(normalized)
        setScheduleId(sData[0]._id)
        if (sData[0].cronConfig) setCronConfig(sData[0].cronConfig)
      }
      setCategories(catRes.data.categories || [])
    } catch (error) {
      console.error(error)
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchData()
  }, [])

  const generateClasses = async () => {
    setGenerating(true)
    try {
      // Si se ha marcado, primero limpia las clases futuras sin inscripciones
      // (útil para borrar las mal generadas antes de regenerar)
      let cleanupMsg = ''
      if (cleanFirst) {
        const cleanRes = await axios.post(
          `${constants.urlLocal}events/cleanup`,
          {}
        )
        cleanupMsg = `${cleanRes.data?.deleted ?? 0} eliminadas. `
      }

      const res = await axios.post(`${constants.urlLocal}events/generate`, {
        weeks,
      })
      toast({
        title: 'Clases generadas',
        description: `${cleanupMsg}${res.data?.message || ''}`,
        status: 'success',
      })
      setGenerateOpen(false)
    } catch (error) {
      toast({
        title: 'Error al generar las clases',
        description:
          error.response?.data?.message || 'Inténtalo de nuevo más tarde',
        status: 'error',
      })
    }
    setGenerating(false)
  }

  const handleAddTime = (categoryName, dayIndex) => {
    const updated = { ...schedules }
    if (!updated[categoryName]) updated[categoryName] = {}
    if (!updated[categoryName][dayIndex]) updated[categoryName][dayIndex] = []
    updated[categoryName][dayIndex] = [
      ...updated[categoryName][dayIndex],
      { hour: '10', minute: '00' },
    ]
    setSchedules(updated)
  }

  const handleRemoveTime = (categoryName, dayIndex, timeIndex) => {
    const updated = { ...schedules }
    updated[categoryName][dayIndex] = updated[categoryName][dayIndex].filter(
      (_, i) => i !== timeIndex
    )
    if (updated[categoryName][dayIndex].length === 0)
      delete updated[categoryName][dayIndex]
    setSchedules(updated)
  }

  const handleModifyTime = (
    categoryName,
    dayIndex,
    timeIndex,
    field,
    value
  ) => {
    const updated = { ...schedules }
    updated[categoryName][dayIndex][timeIndex][field] = value
    setSchedules(updated)
  }

  const saveSchedule = async () => {
    try {
      if (scheduleId) {
        await axios.put(`${constants.urlLocal}schedule/update/${scheduleId}`, {
          schedule: schedules,
          cronConfig,
        })
      } else {
        const res = await axios.post(`${constants.urlLocal}schedule/create`, {
          schedule: schedules,
          cronConfig,
        })
        setScheduleId(res.data._id)
      }
      toast({ title: 'Horario guardado', status: 'success' })
    } catch (error) {
      toast({ title: 'Error al guardar', status: 'error' })
    }
  }

  if (loading)
    return (
      <div className="loading-center">
        <PageLoader />
      </div>
    )

  return (
    <div className="page-container">
      <div className="flex-between" style={{ marginBottom: '1.5rem' }}>
        <h2 className="page-title" style={{ marginBottom: 0 }}>
          Horarios y Clases
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            className="btn btn--secondary"
            onClick={() => setGenerateOpen(true)}
          >
            ⚡ Generar clases
          </button>
          <button className="btn btn--primary" onClick={saveSchedule}>
            Guardar cambios
          </button>
        </div>
      </div>

      {categories.length === 0 ? (
        <p className="empty-state">No hay categorías creadas</p>
      ) : (
        <div className="schedule-grid">
          {categories.map(cat => (
            <div key={cat._id} className="schedule-category">
              <h3
                className="schedule-category__title"
                style={{ borderLeftColor: cat.color }}
              >
                {cat.name}
              </h3>
              <div className="schedule-days">
                {dayNames.map((dayName, dayIndex) => {
                  const times =
                    (schedules[cat.name] && schedules[cat.name][dayIndex]) || []
                  return (
                    <div key={dayIndex} className="schedule-day">
                      <span className="schedule-day__name">{dayName}</span>
                      <div className="schedule-day__times">
                        {times.map((time, tIndex) => (
                          <div key={tIndex} className="schedule-time">
                            <input
                              type="number"
                              className="schedule-time__input"
                              min="0"
                              max="23"
                              value={time.hour}
                              onChange={e =>
                                handleModifyTime(
                                  cat.name,
                                  dayIndex,
                                  tIndex,
                                  'hour',
                                  e.target.value
                                )
                              }
                            />
                            <span>:</span>
                            <input
                              type="number"
                              className="schedule-time__input"
                              min="0"
                              max="59"
                              step="5"
                              value={time.minute}
                              onChange={e =>
                                handleModifyTime(
                                  cat.name,
                                  dayIndex,
                                  tIndex,
                                  'minute',
                                  e.target.value
                                )
                              }
                            />
                            <button
                              className="schedule-time__remove"
                              onClick={() =>
                                handleRemoveTime(cat.name, dayIndex, tIndex)
                              }
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        <button
                          className="schedule-day__add"
                          onClick={() => handleAddTime(cat.name, dayIndex)}
                        >
                          + Añadir
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Configuración del cron automático ── */}
      <div
        style={{
          marginTop: '2rem',
          padding: '1.25rem 1.5rem',
          border: '1px solid #e0e0e0',
          borderRadius: '10px',
          background: '#fafafa',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem' }}>
          Generación automática (cron)
        </h3>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontWeight: 600, fontSize: '0.9rem' }}>
            Día de generación
            <select
              value={cronConfig.dayOfWeek}
              onChange={e => setCronConfig(c => ({ ...c, dayOfWeek: Number(e.target.value) }))}
              style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid #ccc', fontWeight: 'normal' }}
            >
              {dayNames.map((name, i) => (
                <option key={i} value={i}>{name}</option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontWeight: 600, fontSize: '0.9rem' }}>
            Semanas a generar
            <input
              type="number"
              min="1"
              max="12"
              value={cronConfig.weeksAhead}
              onChange={e => setCronConfig(c => ({ ...c, weeksAhead: Math.min(12, Math.max(1, Number(e.target.value) || 1)) }))}
              style={{ width: '70px', padding: '0.35rem', borderRadius: '6px', border: '1px solid #ccc' }}
            />
          </label>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#888', alignSelf: 'flex-end', paddingBottom: '0.1rem' }}>
            Guarda los cambios para que el cron use esta configuración.
          </p>
        </div>
      </div>

      <Modal
        isOpen={generateOpen}
        onClose={() => !generating && setGenerateOpen(false)}
        title="Generar clases automáticamente"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setGenerateOpen(false)}
              disabled={generating}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={generateClasses}
              loading={generating}
            >
              Generar
            </Button>
          </>
        }
      >
        <p style={{ marginBottom: '1rem' }}>
          Se crearán las clases de las próximas semanas según el horario
          guardado. Las clases que ya existan no se duplicarán.
        </p>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontWeight: 600,
          }}
        >
          Semanas a generar:
          <input
            type="number"
            min="1"
            max="12"
            value={weeks}
            onChange={e =>
              setWeeks(Math.min(12, Math.max(1, Number(e.target.value) || 1)))
            }
            style={{
              width: '64px',
              padding: '0.35rem',
              border: '1px solid #ccc',
              borderRadius: '6px',
            }}
          />
        </label>
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5rem',
            marginTop: '1rem',
            fontSize: '0.85rem',
          }}
        >
          <input
            type="checkbox"
            checked={cleanFirst}
            onChange={e => setCleanFirst(e.target.checked)}
            style={{ marginTop: '0.2rem' }}
          />
          <span>
            Limpiar antes las clases futuras <strong>sin inscripciones</strong>{' '}
            (recomendado si hay clases mal generadas). No se borran las clases
            con gente apuntada.
          </span>
        </label>
        <p
          style={{
            marginTop: '0.75rem',
            fontSize: '0.8rem',
            color: '#888',
          }}
        >
          Recuerda guardar los cambios del horario antes de generar.
        </p>
      </Modal>
    </div>
  )
}
